import axios, { AxiosResponse, AxiosError, AxiosInstance } from "axios";
import { Authorization } from "./authenticate";

class ApiConnector {
  private readonly axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
    });

    this.axiosInstance.interceptors.request.use((config) => {
      config.headers.Authorization = `Bearer ${Authorization.token}`;
      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config;

        if (status === 401 && originalRequest && !originalRequest.__isRetry) {
          originalRequest.__isRetry = true;

          try {
            if (Authorization.tokenLooksExpired()) {
              const refreshed = await Authorization.refreshTokenIfExpired();
              if (refreshed) {
                originalRequest.headers.Authorization = `Bearer ${Authorization.token}`;
                return this.axiosInstance(originalRequest);
              }
            }

            const reLoggedIn = await Authorization.reLogIn();

            if (reLoggedIn) {
              originalRequest.headers.Authorization = `Bearer ${Authorization.token}`;
              return this.axiosInstance(originalRequest);
            }
          } catch (reloginError) {
            console.error("Failed to re-login after 401", reloginError);
          }

          const currentPath = window.location.pathname;
          if (currentPath !== "/login") {
            sessionStorage.setItem(
              "log_to",
              currentPath === "/" ? "ReportHours" : currentPath.replace("/", "") || "ReportHours"
            );
            window.location.href = `${window.location.origin}/login`;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async request<T>(
    method: string,
    endpoint: string,
    data?: any
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance({
        method,
        url: endpoint,
        data,
      });
      return response.data;
    } catch (error: any) {
      throw (error as AxiosError).response?.data || error.message;
    }
  }
  private async requestBlob<T>(
    method: string,
    endpoint: string,
    data?: any,    
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance({
        method,
        url: endpoint,
        data,
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      throw (error as AxiosError).response?.data || error.message;
    }
  }

  public async getBlob<T>(endpoint: string): Promise<T> {
    return await this.requestBlob<T>("GET", endpoint);
  }

  public async get<T>(endpoint: string): Promise<T> {
    return await this.request<T>("GET", endpoint);
  }

  public async put<T>(endpoint: string, data: any): Promise<T> {
    return await this.request<T>("PUT", endpoint, data);
  }

  public async post<T>(endpoint: string, data?: any): Promise<T> {
    return await this.request<T>("POST", endpoint, data);
  }

  public async delete<T>(endpoint: string): Promise<T> {
    return await this.request<T>("DELETE", endpoint);
  }
}

export default ApiConnector;
